import { trackEvent, analytics } from "../lib/analytics";
import { environmentURLs } from "../utils/environmentURLs";
import { getCookie, setCookie } from "./cookieHelpers";
const { frontendUrl: url } = environmentURLs;

function setCookiesForEnvironment(env, token, _id) {
  let domain;
  if (env?.startsWith("dev")) {
    domain = ".codedesign.app";
    setCookie("token", token, domain);
    setCookie("userId", _id, domain);
    domain = "staging-accounts.codedesign.app";
    setCookie("token", token, domain);
    setCookie("userId", _id, domain);
    domain = ".cdsn.me";
    setCookie("token", token, domain);
    setCookie("userId", _id, domain);
  } else if (env?.startsWith("prod")) {
    domain = ".codedesign.ai";
    setCookie("token", token, domain);
    setCookie("userId", _id, domain);
    domain = "accounts.codedesign.ai";
    setCookie("token", token, domain);
    setCookie("userId", _id, domain);
  }
}

export const generateUrl = async (user, redirect = null) => {
  const { _id, token } = user;
  const environment = process.env.REACT_APP_ENV || "development";

  setCookiesForEnvironment(environment, token, _id);

  await checkSessionAndUserId(_id);
  const uRI = _id;

  // Rewrite pricing/paymentId URLs to dashboard domain
  if (redirect && redirect.startsWith('http')) {
    try {
      const redirectUrl = new URL(redirect);
      if (redirectUrl.pathname === '/pricing' && redirectUrl.searchParams.has('paymentId')) {
        const currentHost = window.location.hostname;
        const dashboardHost = currentHost === 'accounts.codedesign.ai'
          ? 'dashboard.codedesign.ai'
          : 'dashboard.cdsn.me';
        redirectUrl.hostname = dashboardHost;
        redirectUrl.protocol = 'https:';
        window.location = redirectUrl.toString();
        return;
      }
    } catch (e) {
      // If URL parsing fails, fall through to existing logic
    }
  }

  // If redirect is a full URL, use it directly
  if (redirect && redirect.startsWith('http')) {
    window.location = redirect;
    return;
  }

  let finalRedirect = redirect;

  if (!finalRedirect) {
    const createdAt = user?.timestamps?.createdAt;
    if (createdAt) {
      const createdAtDate = new Date(createdAt);
      const currentDate = new Date();
      const differenceInMilliseconds = currentDate - createdAtDate;
      const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
      
      if (differenceInMilliseconds < oneDayInMilliseconds) {
        finalRedirect = "/ai";
      } else {
        finalRedirect = "/a/dashboard";
      }
    } else {
       // Fallback if no timestamp (e.g. valid existing user but missing field, or new user), default to /ai as safe bet for "signup like" flow or /a/dashboard? 
       // User asked "if their account was created in less than 1 day, then /ai. otherwise / or /a/dashboard."
       // If missing `createdAt`, it might be an old user or a very new one. 
       // Let's assume /a/dashboard for safety if missing, or maybe /ai if we want to push them there.
       // Given the context of "signup", usually createdAt exists.
       finalRedirect = "/a/dashboard"; 
    }
  }

  // Otherwise, construct the URL with the user ID and parameters
  let authURL = `${url}/${uRI}?action=login&uRI=${uRI}&redirect=${encodeURIComponent(
      finalRedirect
    )}`;
  
  trackEvent("Session:RedirectStarted", {
    redirectTo: finalRedirect,
    hasRedirect: !!redirect,
  });
  window.location = authURL;
};

async function checkSessionAndUserId(_id) {
  try {
    // Retrieve userId from analytics state or cookie
    const analyticsState = analytics.getState(); // Handle the analytics state
    const userIdFromAnalytics = analyticsState
      ? analyticsState.user.userId
      : null;
    const userIdFromCookie = getCookie("userId");
    let userId = userIdFromAnalytics || userIdFromCookie || _id;

    // Check for userId in localStorage if not found in analytics state or cookie
    if (!userId) {
      userId = localStorage.getItem("uRI");
    }

    // Identify the user in analytics if userId is available
    if (userId) {
      await analytics.identify(userId); // Ensure identify is handled asynchronously

      // Track events after successful identification
      trackEvent("Session:UserIdentified", { userId });
    }
  } catch (err) {
    console.error("An error occurred within the analytics module", err);
  }
}
